import {
  pushThemeGtmEvent,
  getAntiforgeryToken,
  loadRecaptchaScript,
  getRecaptchaTokenForForm,
} from './theme-utils.ts';

declare global {
  interface Window {
    afHeaderResizeBound?: boolean;
    afAnnounceDismissBound?: boolean;
    afHideEmptyValidationSummaries?: () => void;
    afCleanupEmptyFlashMessages?: () => void;
  }
}

// Dynamic announcement bar and nav height offsets for page layout
export function measureAndApplyOffsets(): void {
  // Phase 1: Batch all DOM layout reads first (zero style invalidation during reads)
  const bar =
    document.querySelector<HTMLElement>('[data-widget-type="AnnouncementBar"]') ||
    document.querySelector<HTMLElement>('[role="banner"]');
  let announceHeight = 0;
  if (
    bar &&
    bar.offsetWidth > 0 &&
    bar.offsetHeight > 0 &&
    window.getComputedStyle(bar).display !== 'none'
  ) {
    announceHeight = bar.offsetHeight;
  }

  const nav =
    document.getElementById('af-main-nav') ||
    document.querySelector<HTMLElement>('[data-widget-type="Nav"]') ||
    document.querySelector<HTMLElement>('header nav');
  let navHeight = 0;
  if (
    nav &&
    nav.offsetWidth > 0 &&
    nav.offsetHeight > 0 &&
    window.getComputedStyle(nav).display !== 'none'
  ) {
    navHeight = nav.offsetHeight;
  }

  // Phase 2: Batch CSS variable writes only if differing from CSS defaults
  if (announceHeight > 0) {
    document.documentElement.style.setProperty('--af-announce-height', announceHeight + 'px');
  }
  if (navHeight > 0 && navHeight !== 64) {
    document.documentElement.style.setProperty('--af-nav-height', navHeight + 'px');
  }
}

export function updateHeaderOffsets(): void {
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    window.requestAnimationFrame(measureAndApplyOffsets);
  } else {
    measureAndApplyOffsets();
  }
}

export function initNavFavicon(root: Document | HTMLElement = document): void {
  if (typeof document === 'undefined') return;
  const nav =
    root.querySelector<HTMLElement>('#af-main-nav') ||
    root.querySelector<HTMLElement>('[data-widget-type="Nav"]') ||
    root.querySelector<HTMLElement>('nav[data-favicon]');
  if (!nav) return;
  const favicon = nav.getAttribute('data-favicon');
  if (!favicon) return;

  const existingLinks = document.querySelectorAll<HTMLLinkElement>(
    'link[data-nav-favicon], link[rel="icon"], link[rel="apple-touch-icon"]'
  );
  if (existingLinks.length > 0) {
    existingLinks.forEach((link) => {
      link.href = favicon;
    });
  } else {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.setAttribute('data-nav-favicon', 'true');
    link.href = favicon;
    document.head.appendChild(link);
  }
}

export function initAnnouncementBars(): void {
  const announcementBars = document.querySelectorAll<HTMLElement>(
    '[data-widget-type="AnnouncementBar"], [role="banner"]'
  );
  announcementBars.forEach(function (bar) {
    const barId = bar.id || 'acms-announce-bar';
    try {
      if (
        localStorage.getItem('acms-bar-dismissed-' + barId) === '1' ||
        sessionStorage.getItem('acms_dismiss_' + barId) === 'true'
      ) {
        bar.style.display = 'none';
        document.documentElement.style.setProperty('--af-announce-height', '0px');
      }
    } catch {}
  });

  updateHeaderOffsets();
  if (typeof window !== 'undefined' && !window.afHeaderResizeBound) {
    window.afHeaderResizeBound = true;
    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    window.addEventListener(
      'resize',
      function () {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(updateHeaderOffsets, 100);
      },
      { passive: true }
    );
  }

  if (typeof window !== 'undefined' && !window.afAnnounceDismissBound) {
    window.afAnnounceDismissBound = true;
    document.addEventListener('click', function (e) {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const btn = target.closest<HTMLElement>(
        '[data-dismiss], #af-announce-dismiss, [aria-label*="Dismiss"], .announcement-dismiss'
      );
      if (!btn) return;
      const bar =
        btn.closest<HTMLElement>(
          '[data-widget-type="AnnouncementBar"], [role="banner"], [id^="acms-announce"], .acms-announce-bar'
        ) || btn.closest<HTMLElement>('[id]');
      if (bar) {
        e.preventDefault();
        e.stopPropagation();
        bar.style.display = 'none';
        document.documentElement.style.setProperty('--af-announce-height', '0px');
        const barId = bar.id || 'acms-announce-bar';
        try {
          localStorage.setItem('acms-bar-dismissed-' + barId, '1');
          sessionStorage.setItem('acms_dismiss_' + barId, 'true');
        } catch {}
        updateHeaderOffsets();
      }
    });
  }
}

// Hide validation summary container when valid / empty of errors
export function hideEmptyValidationSummaries(): void {
  const summaries = document.querySelectorAll<HTMLElement>(
    '.validation-summary-container, [data-valmsg-summary="true"], .validation-summary-valid'
  );
  summaries.forEach(function (el) {
    const hasErrors = el.classList.contains('validation-summary-errors');
    const lis = Array.from(el.querySelectorAll('li')).filter(function (li) {
      return li.style.display !== 'none' && (li.textContent || '').trim().length > 0;
    });
    const hasText = (el.textContent || '').trim().length > 0;
    if (!hasErrors || (!hasText && lis.length === 0)) {
      el.style.display = 'none';
    } else {
      el.style.display = 'block';
    }
  });
}

// Clean up empty flash messages
export function cleanupEmptyFlashMessages(): void {
  document
    .querySelectorAll<HTMLElement>('.alert, [class*="alert-"], .flash-message')
    .forEach(function (el) {
      if (
        el.classList.contains('validation-summary-container') ||
        el.hasAttribute('data-valmsg-summary')
      )
        return;
      if ((el.textContent || '').trim().length === 0 && el.children.length === 0) {
        el.remove();
      }
    });
  document.querySelectorAll<HTMLElement>('.flash-messages-container').forEach(function (c) {
    if ((c.textContent || '').trim().length === 0 && c.children.length === 0) {
      c.style.display = 'none';
    }
  });
}

// Auth form tab switching
export function initAuthTabSwitchers(): void {
  const tabs = document.querySelectorAll<HTMLElement>('.acms-auth-tab-btn');
  const panels = document.querySelectorAll<HTMLElement>('.acms-auth-panel');
  const switcherBtns = document.querySelectorAll<HTMLElement>('.acms-switch-tab-btn');

  function selectTab(targetBtn: HTMLElement | null): void {
    if (!targetBtn) return;
    const targetPanelId = targetBtn.getAttribute('aria-controls');

    tabs.forEach(function (t) {
      t.setAttribute('aria-selected', 'false');
      t.classList.remove(
        'bg-white',
        'dark:bg-slate-800',
        'text-slate-900',
        'dark:text-slate-100',
        'font-semibold',
        'shadow-xs'
      );
      t.classList.add('text-slate-600', 'dark:text-slate-400');
    });

    panels.forEach(function (p) {
      p.classList.add('hidden');
    });

    targetBtn.setAttribute('aria-selected', 'true');
    targetBtn.classList.remove('text-slate-600', 'dark:text-slate-400');
    targetBtn.classList.add(
      'bg-white',
      'dark:bg-slate-800',
      'text-slate-900',
      'dark:text-slate-100',
      'font-semibold',
      'shadow-xs'
    );

    if (targetPanelId) {
      const activePanel = document.getElementById(targetPanelId);
      if (activePanel) {
        activePanel.classList.remove('hidden');
      }
    }
  }

  tabs.forEach(function (btn) {
    btn.addEventListener('click', function () {
      selectTab(btn);
    });
  });

  switcherBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      const targetId = btn.getAttribute('data-target-tab');
      const matchTab = Array.from(tabs).find(function (t) {
        return t.getAttribute('aria-controls') === targetId;
      });
      if (matchTab) selectTab(matchTab);
    });
  });
}

// Password visibility toggle logic
export function initPasswordToggles(): void {
  document.querySelectorAll('[class*="acms-toggle-pwd-btn"]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const inputId = btn.getAttribute('data-target');
      if (!inputId) return;
      const input = document.getElementById(inputId) as HTMLInputElement | null;
      const eyeOpen = btn.querySelector('.acms-eye-open');
      const eyeClosed = btn.querySelector('.acms-eye-closed');

      if (input) {
        if (input.type === 'password') {
          input.type = 'text';
          if (eyeOpen) eyeOpen.classList.add('hidden');
          if (eyeClosed) eyeClosed.classList.remove('hidden');
        } else {
          input.type = 'password';
          if (eyeOpen) eyeOpen.classList.remove('hidden');
          if (eyeClosed) eyeClosed.classList.add('hidden');
        }
      }
    });
  });
}

// Carousel initializer
export function initCarousels(): void {
  document.querySelectorAll<HTMLElement>('[id^="acms-carousel-"]').forEach(function (carousel) {
    if (carousel.dataset.carouselBound === 'true') return;
    carousel.dataset.carouselBound = 'true';

    const track = carousel.querySelector<HTMLElement>('[data-carousel-track]');
    if (!track) return;
    const slides = Array.from(track.children) as HTMLElement[];
    const total = slides.length;
    if (total < 2) return;

    let current = 0;
    const dotsContainer = carousel.querySelector('[data-dots-container]');
    let autoTimer: ReturnType<typeof setInterval> | null = null;

    slides.forEach(function (s) {
      s.classList.add('w-full', 'flex-shrink-0');
    });

    const dots = dotsContainer
      ? slides.map(function (item, i) {
          const dot = document.createElement('button');
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          dot.className =
            'h-2.5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer ' +
            (i === 0 ? 'w-6 bg-indigo-500' : 'w-2.5 bg-slate-600 hover:bg-slate-500');
          dot.addEventListener('click', function () {
            stopAuto();
            goTo(i);
            startAuto();
          });
          dotsContainer.appendChild(dot);
          return dot;
        })
      : [];

    function goTo(index: number): void {
      current = (index + total) % total;
      track!.style.transform = 'translateX(-' + current * 100 + '%)';
      dots.forEach(function (d, i) {
        d.className =
          'h-2.5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer ' +
          (i === current ? 'w-6 bg-indigo-500' : 'w-2.5 bg-slate-600 hover:bg-slate-500');
      });
    }
    function startAuto(): void {
      stopAuto();
      autoTimer = setInterval(function () {
        goTo(current + 1);
      }, 5000);
    }
    function stopAuto(): void {
      if (autoTimer) {
        clearInterval(autoTimer);
        autoTimer = null;
      }
    }

    const prevBtn = carousel.querySelector('[data-prev]');
    const nextBtn = carousel.querySelector('[data-next]');
    if (prevBtn)
      prevBtn.addEventListener('click', function () {
        stopAuto();
        goTo(current - 1);
        startAuto();
      });
    if (nextBtn)
      nextBtn.addEventListener('click', function () {
        stopAuto();
        goTo(current + 1);
        startAuto();
      });

    carousel.addEventListener('mouseenter', stopAuto);
    carousel.addEventListener('mouseleave', startAuto);

    startAuto();
  });
}

// Contact form submit listener
export function initContactForm(): void {
  const anyCaptchaForm = document.querySelector<HTMLFormElement>(
    'form[data-captcha-enabled="true"]'
  );
  if (anyCaptchaForm) {
    const siteKey =
      anyCaptchaForm.dataset.recaptchaSiteKey ||
      anyCaptchaForm.getAttribute('data-recaptcha-site-key') ||
      '';
    if (siteKey) {
      loadRecaptchaScript(siteKey).catch(() => {});
    }
  }

  const contactForm = document.getElementById('acms-contact-form') as HTMLFormElement | null;
  if (contactForm && contactForm.dataset.asyncBound !== 'true') {
    contactForm.dataset.asyncBound = 'true';
    const contactResult = document.getElementById('acms-contact-result');
    const contactBtn = document.getElementById('acms-contact-submit') as HTMLButtonElement | null;
    contactForm.addEventListener('submit', async function (e: Event): Promise<void> {
      e.preventDefault();
      if (contactBtn) contactBtn.disabled = true;

      const captchaToken = await getRecaptchaTokenForForm(contactForm, 'contact');
      if (captchaToken) {
        const hiddenToken = contactForm.querySelector<HTMLInputElement>(
          'input[name="RecaptchaToken"]'
        );
        if (hiddenToken) {
          hiddenToken.value = captchaToken;
        }
      }

      const data = new FormData(contactForm);
      const token = getAntiforgeryToken(contactForm);
      const headers: Record<string, string> = {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      };
      if (token) {
        headers['RequestVerificationToken'] = token;
        headers['X-XSRF-TOKEN'] = token;
        headers['X-CSRF-TOKEN'] = token;
      }
      const params = new URLSearchParams();
      data.forEach(function (val, key) {
        if (typeof val === 'string') {
          params.append(key, val);
        }
      });
      if (token && !params.has('__RequestVerificationToken')) {
        params.append('__RequestVerificationToken', token);
      }
      if (captchaToken) {
        params.set('RecaptchaToken', captchaToken);
        params.set('g-recaptcha-response', captchaToken);
      }
      fetch(contactForm.action, {
        method: 'POST',
        body: params,
        headers,
        credentials: 'same-origin',
      })
        .then(function (r) {
          return r.json().then(function (j) {
            return { ok: r.ok, body: j };
          });
        })
        .then(function (res) {
          if (contactResult) {
            contactResult.classList.remove(
              'hidden',
              'bg-green-500/10',
              'text-green-400',
              'bg-red-500/10',
              'text-red-400'
            );
            if (res.ok && res.body.success) {
              contactResult.classList.add('bg-green-500/10', 'text-green-400');
              contactResult.textContent =
                res.body.message || "Thank you! We'll be in touch shortly.";
              contactForm.reset();
              pushThemeGtmEvent('generate_lead', {
                form_name: 'contact_form',
                form_destination: contactForm.action,
              });
            } else {
              contactResult.classList.add('bg-red-500/10', 'text-red-400');
              const errs = (res.body.errors || ['Something went wrong. Please try again.']).join(
                ' '
              );
              contactResult.textContent = errs;
              if (contactBtn) contactBtn.disabled = false;
            }
          }
        })
        .catch(function () {
          if (contactResult) {
            contactResult.classList.remove('hidden', 'bg-green-500/10', 'text-green-400');
            contactResult.classList.add('bg-red-500/10', 'text-red-400');
            contactResult.textContent = 'Network error. Please try again.';
          }
          if (contactBtn) contactBtn.disabled = false;
        });
    });
  }
}

// Gallery lightbox viewer
export function initLightboxes(): void {
  const section = document.getElementById('acms-gallery-section');
  const overlay = document.getElementById('acms-lightbox');
  const lbImg = document.getElementById('acms-lightbox-img') as HTMLImageElement | null;
  const closeBtn = document.getElementById('acms-lightbox-close');

  if (section && overlay && lbImg) {
    const openLightbox = function (src: string, alt?: string): void {
      lbImg.src = src;
      lbImg.alt = alt || '';
      overlay.classList.remove('hidden');
      overlay.classList.add('grid');
      document.body.style.overflow = 'hidden';
    };

    const closeLightbox = function (): void {
      overlay.classList.remove('grid');
      overlay.classList.add('hidden');
      document.body.style.overflow = '';
      lbImg.src = '';
    };

    section.querySelectorAll('img').forEach(function (img) {
      img.style.cursor = 'pointer';
      img.addEventListener('click', function () {
        openLightbox(img.src, img.alt);
      });
    });

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeLightbox();
    });

    if (closeBtn) closeBtn.addEventListener('click', closeLightbox);

    document.addEventListener('keydown', function (e: KeyboardEvent): void {
      if (e.key === 'Escape') closeLightbox();
    });
  }
}

// Newsletter form submission listener
export function initNewsletterForm(): void {
  const newsletterForm = document.getElementById('acms-newsletter-form') as HTMLFormElement | null;
  if (newsletterForm && newsletterForm.dataset.asyncBound !== 'true') {
    newsletterForm.dataset.asyncBound = 'true';
    const newsletterResult = document.getElementById('acms-newsletter-result');
    const newsletterBtn = document.getElementById(
      'acms-newsletter-submit'
    ) as HTMLButtonElement | null;
    newsletterForm.addEventListener('submit', async function (e: Event): Promise<void> {
      e.preventDefault();
      if (newsletterBtn) newsletterBtn.disabled = true;

      const captchaToken = await getRecaptchaTokenForForm(newsletterForm, 'newsletter');
      if (captchaToken) {
        const hiddenToken = newsletterForm.querySelector<HTMLInputElement>(
          'input[name="RecaptchaToken"]'
        );
        if (hiddenToken) {
          hiddenToken.value = captchaToken;
        }
      }

      const data = new FormData(newsletterForm);
      const token = getAntiforgeryToken(newsletterForm);
      const headers: Record<string, string> = {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      };
      if (token) {
        headers['RequestVerificationToken'] = token;
        headers['X-XSRF-TOKEN'] = token;
        headers['X-CSRF-TOKEN'] = token;
      }
      const params = new URLSearchParams();
      data.forEach(function (val, key) {
        if (typeof val === 'string') {
          params.append(key, val);
        }
      });
      if (token && !params.has('__RequestVerificationToken')) {
        params.append('__RequestVerificationToken', token);
      }
      if (captchaToken) {
        params.set('RecaptchaToken', captchaToken);
        params.set('g-recaptcha-response', captchaToken);
      }
      fetch(newsletterForm.action, {
        method: 'POST',
        body: params,
        headers,
        credentials: 'same-origin',
      })
        .then(function (r) {
          return r.json().then(function (j) {
            return { ok: r.ok, body: j };
          });
        })
        .then(function (res) {
          if (newsletterResult) {
            newsletterResult.classList.remove(
              'hidden',
              'bg-green-500/10',
              'text-green-400',
              'bg-red-500/10',
              'text-red-400'
            );
            if (res.ok && res.body.success) {
              newsletterResult.classList.add('bg-green-500/10', 'text-green-400');
              newsletterResult.textContent = res.body.message || 'You are subscribed!';
              newsletterForm.reset();
              pushThemeGtmEvent('sign_up', {
                method: 'newsletter_form',
              });
            } else {
              newsletterResult.classList.add('bg-red-500/10', 'text-red-400');
              const errs = (res.body.errors || ['Something went wrong. Please try again.']).join(
                ' '
              );
              newsletterResult.textContent = errs;
              if (newsletterBtn) newsletterBtn.disabled = false;
            }
          }
        })
        .catch(function () {
          if (newsletterResult) {
            newsletterResult.classList.remove('hidden', 'bg-green-500/10', 'text-green-400');
            newsletterResult.classList.add('bg-red-500/10', 'text-red-400');
            newsletterResult.textContent = 'Network error. Please try again.';
          }
          if (newsletterBtn) newsletterBtn.disabled = false;
        });
    });
  }
}

// Preview Studio postMessage listener
export function initPreviewStudioListener(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('message', function (e: MessageEvent): void {
    if (!e.data || typeof e.data !== 'object') return;
    if (e.data.action === 'toggleAnimations') {
      if (window.afToggleAnimations) {
        window.afToggleAnimations(e.data.enabled);
      }
    }
  });
}
